import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useOrgChat, type PageContext } from '../hooks/useOrgChat';
import { RbChatMessageList } from '../components/RbChatMessageList';

function readPageContext(searchParams: URLSearchParams): PageContext | undefined {
  const candidateId = searchParams.get('candidateId');
  if (candidateId && Number.isFinite(Number(candidateId))) {
    return { type: 'candidate', id: Number(candidateId) };
  }
  const jobId = searchParams.get('jobId');
  if (jobId && Number.isFinite(Number(jobId))) {
    return { type: 'job', id: Number(jobId) };
  }
  return undefined;
}

const SUGGESTED_PROMPTS = [
  'Who are my top candidates for [job]?',
  'Any new applicants today?',
  "Summarize Jane Smith's latest review",
];

export default function RbChat() {
  const { conversationId, messages, isStreaming, streamingContent, error, startConversation, sendMessage } = useOrgChat();
  const [input, setInput] = useState('');
  const [searchParams] = useSearchParams();
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: 'end' });
  }, [messages, streamingContent]);


  useEffect(() => {
    if (!conversationId) {
      startConversation(readPageContext(searchParams));
    }
  }, [conversationId, startConversation, searchParams]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim()) return;
    sendMessage(input);
    setInput('');
  };

  return (
    <div className="flex h-dvh flex-col overflow-hidden bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100">
      <header className="flex items-center justify-between border-b border-gray-200 dark:border-gray-800 px-4 py-3">
        <span className="font-semibold">Ask RB</span>
      </header>

      <main className="flex-1 overflow-y-auto">
        {messages.length === 0 && !isStreaming && (
          <div className="flex flex-col gap-2 p-4 text-sm text-gray-500 dark:text-gray-400">
            <p>Ask me anything about your candidates, jobs, or applications.</p>
            {SUGGESTED_PROMPTS.map((prompt) => (
              <button
                key={prompt}
                type="button"
                onClick={() => setInput(prompt)}
                className="rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-800"
              >
                {prompt}
              </button>
            ))}
          </div>
        )}
        <RbChatMessageList messages={messages} streamingContent={streamingContent} />
        {error && <p className="px-4 text-sm text-red-400">{error}</p>}
        <div ref={bottomRef} />
      </main>

      <form onSubmit={handleSubmit} className="flex gap-2 border-t border-gray-200 dark:border-gray-800 p-4">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Ask about a candidate or job..."
          className="flex-1 rounded-lg bg-white dark:bg-gray-800 px-3 py-2 text-sm outline-none"
          disabled={isStreaming}
        />
        <button type="submit" disabled={isStreaming || !input.trim()} className="rounded-lg bg-sky-600 px-4 py-2 text-sm font-medium disabled:opacity-50">
          Send
        </button>
      </form>
    </div>
  );
}
